import {
  Check,
  ChevronsUpDown,
  CircleCheck,
  CircleX,
  Webhook,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { SidebarMenuButton } from '@/components/sv/sidebar'
import { BreadcrumbItem } from '@/components/ui/breadcrumb'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery, useIdCompany } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  WebhookSwitcherQuery,
  WebhookSwitcherQueryVariables,
} from '@/components/layout/breadcrumbs/webhook/webhook-switcher/switcher.types.gen'

const WEBHOOK_SWITCHER_QUERY = gql`
  query WebhookSwitcher($idCompany: String!) {
    company(id: $idCompany) {
      id
      webhooks {
        id
        total
        items {
          id
          endpoint
          isEnabled
        }
      }
    }
  }
`

export const WebhookSwitcherData = () => {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const { idWebhook = '' } = useParams()
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    WebhookSwitcherQuery,
    WebhookSwitcherQueryVariables
  >(WEBHOOK_SWITCHER_QUERY, {
    variables: { idCompany },
  })

  const webhooks = data?.company?.webhooks?.items ?? []

  const activeWebhook =
    webhooks.find((webhook) => webhook.id === idWebhook) || webhooks[0]

  return (
    <BreadcrumbItem>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <SidebarMenuButton
            size="lg"
            aria-expanded={open}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <div className="grid flex-1 text-left text-sm leading-tight">
              <div className="flex items-center gap-2">
                {activeWebhook?.isEnabled ? (
                  <CircleCheck className="size-4 fill-emerald-600 stroke-background" />
                ) : (
                  <CircleX className="size-4 fill-destructive stroke-background" />
                )}
                <span
                  title={activeWebhook?.endpoint ?? ''}
                  className="max-w-[250px] truncate text-foreground font-medium"
                >
                  {activeWebhook?.endpoint ?? ''}
                </span>
              </div>
            </div>
            <ChevronsUpDown className="ml-auto" />
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          className="w-max min-w-[max(250px,var(--radix-popper-anchor-width))] max-w-[480px] p-0"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <Command>
            <CommandInput placeholder="Search webhook..." className="h-9" />
            <CommandList>
              <CommandEmpty>No webhook found</CommandEmpty>
              <CommandGroup>
                {webhooks.map((webhook) => (
                  <Link
                    key={webhook.id}
                    to={`${pathname.replace(idWebhook, webhook.id)}?idCompany=${idCompany}`}
                    onClick={() => setOpen(false)}
                  >
                    <CommandItem
                      value={webhook.id}
                      keywords={[webhook.endpoint]}
                      className="cursor-pointer"
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border">
                        {activeWebhook?.id === webhook.id ? (
                          <Check className="size-3.5 shrink-0" />
                        ) : (
                          <Webhook className="size-3.5 shrink-0" />
                        )}
                      </div>
                      <span
                        className={cn('truncate', {
                          'font-medium': activeWebhook?.id === webhook?.id,
                        })}
                      >
                        {webhook.endpoint}
                      </span>
                    </CommandItem>
                  </Link>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </BreadcrumbItem>
  )
}
